import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SidebarModule } from 'primeng/sidebar';
import { first, map, Observable, of, Subscription, switchMap } from 'rxjs';

import { AdminService, GroupUser } from '../services/admin.service';
import { BulkOperationService, ReassignmentDto } from '../services/bulk-operation.service';
import { NavigationService } from '../services/navigation.service';
import { ReassignmentService } from '../services/reassignment.service';
import { TableConfigBuilder } from '../services/table-config-builder.service';
import { UIManagerService } from '../services/ui-manager.service';
import { UserService } from '../services/user-service';

import { NgClass } from '@angular/common';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PanelModule } from 'primeng/panel';
import { ConfigurableButtonComponent } from '../components/button.component';
import { InfoBarComponent } from '../components/info-bar.component';
import { StepperComponent } from '../components/stepper.component';
import { TableComponent } from '../components/table.component';
import { FuzzyUserSearchComponent } from '../components/user-search.component';
import { CaseRootInstance } from '../interfaces/case-instance.interface';
import { CustomCodeEventDetail, PaginationConfig } from '../interfaces/reassignment.interface';
import { PUITranslatePipe } from '../pipes/p-ui-translate.pipe';

declare var ap: any;
interface UserIcon {
    icon: string;
    value: number;
    label: string;
    color: string;
}

interface User {
    username: string;
    icons: UserIcon[];
}

@Component({
    selector: 'app-reassignment',
    standalone: true,
    styles: [
        `
            :host {
                display: block;
                height: 100%;
                width: 100%;
            }

            .h-90 {
                height: 90% !important;
            }
            .p-20 {
                padding: 1.25rem;
            }
            .px-20 {
                padding-left: 1.25rem;
                padding-right: 1.25rem;
            }
        `,
    ],
    template: `
        @if (ready) {
            <div
                class="w-full h-full flex flex-column shadow-md surface-ground flex-1 flex border-round-sm overflow-auto"
            >
                <div class="flex flex-column">
                    <div class="border-bottom-1 surface-border p-20 font-semibold">
                        {{ 'default-reassign-title' | pTranslate }}
                    </div>
                    <div class="w-full px-20 border-bottom-1 surface-border">
                        <platform-stepper [steps]="steps" [currentStepName]="currentStepName"></platform-stepper>
                    </div>
                </div>
                <div class="flex flex-column flex-1 p-3 gap-3">
                    <platform-info-bar
                        [message]="
                            infoBarMessage
                                | pTranslate
                                    : infoBarMessage
                                    : { totalSelectedCases: reassignmentService.casesToReassign.length || 0 }
                        "
                    ></platform-info-bar>
                    <div class="flex flex-column gap-3 reassignment-cases" #casesContainer>
                        @for (item of groupedCases; track item.group) {
                            <p-panel
                                [toggleable]="true"
                                styleClass="shadow-none surface-border border-1 surface-ground overflow-hidden"
                            >
                                <ng-template pTemplate="header">
                                    <div class="flex align-items-center gap-2 justify-content-between w-full mr-4">
                                        <span class="font-bold capitalize">{{
                                            item.group | pTranslate: item.group
                                        }}</span>
                                        <div class="gap-4 flex">
                                            @if (item.table.config.clearAllButtonConfig) {
                                                <platform-button
                                                    [label]="
                                                        item.table.config.clearAllButtonConfig.buttonLabel | pTranslate
                                                    "
                                                    [outlined]="false"
                                                    [iconPosition]="
                                                        item.table.config.clearAllButtonConfig.iconPosition || 'left'
                                                    "
                                                    [icon]="item.table.config.clearAllButtonConfig.icon || ''"
                                                    [text]="item.table.config.clearAllButtonConfig.text || false"
                                                    [disabled]="
                                                        item.table.config.clearAllButtonConfig.disabled || false
                                                    "
                                                    (buttonClick)="handleClearAllClick(item.group)"
                                                />
                                            }
                                            <platform-button
                                                [buttonType]="'button'"
                                                [label]="item.table.config.bulkButtonConfig?.buttonLabel | pTranslate"
                                                [outlined]="item.table.config.bulkButtonConfig?.outlined"
                                                [styleClass]="'reassign-action-btn'"
                                                [iconPosition]="
                                                    item.table.config.bulkButtonConfig?.iconPosition || 'left'
                                                "
                                                [icon]="item.table.config.bulkButtonConfig?.icon"
                                                (buttonClick)="handleBulkClick(item.group, item.isOwnedTaskGroup)"
                                            ></platform-button>
                                        </div>
                                    </div>
                                </ng-template>
                                <ng-template let-isCollapsed pTemplate="headericons">
                                    <i
                                        class="pi"
                                        [ngClass]="{ 'pi-chevron-up': !isCollapsed, 'pi-chevron-down': isCollapsed }"
                                    ></i>
                                </ng-template>
                                <div class="m-0 surface-border surface-ground">
                                    <div class="overflow-auto max-h-50rem">
                                        <platform-table
                                            [tableConfiguration]="item.table.tableConfiguration"
                                            [enableBulkAction]="true"
                                            [bulkButtonConfig]="item.table.config.bulkButtonConfig"
                                            [data]="item.table.data"
                                            [pageSizeOptions]="item.table.config.pageSizeOptions"
                                            [enableAction]="true"
                                            [selectable]="item.table.config.selectable"
                                            [actionButtonConfig]="item.table.config.actionButtonConfig"
                                            (onAction)="handleSingleClick(item.group, $event, item.isOwnedTaskGroup)"
                                            (afterViewInitEvent)="handleAfterViewInit($event)"
                                            [customClass]="
                                                'reassignment-table group-' +
                                                item.group +
                                                ' ' +
                                                reassignmentService.currentTableClass
                                            "
                                            [enableClearAllAction]="true"
                                            [clearAllButtonConfig]="item.table.config.clearAllButtonConfig"
                                            (pageChange)="onPageChange($event, item.group)"
                                            (pageSizeChange)="onPageSizeChange($event, item.group)"
                                            [pageSize]="this.paginationConfigByGroup.get(item.group)?.pageSize || 10"
                                            [currentPage]="
                                                this.paginationConfigByGroup.get(item.group)?.currentPage || 1
                                            "
                                        >
                                        </platform-table>
                                    </div>
                                </div>
                            </p-panel>
                        }
                    </div>
                </div>
                <div class="flex bottom-0 sticky">
                    <div class="w-full surface-ground border-top-1 surface-border p-3">
                        <div class="flex justify-content-end gap-3">
                            <platform-button
                                [buttonType]="'button'"
                                [label]="'default-reassign-buttonCancel' | pTranslate: 'Cancel'"
                                [outlined]="true"
                                [text]="true"
                                [styleClass]="'reassign-action-btn'"
                                (buttonClick)="reassignmentCancelButtonClick()"
                            ></platform-button>
                            <platform-button
                                [buttonType]="'button'"
                                [styleClass]="'reassign-action-btn'"
                                [label]="'default-reassign-buttonNext' | pTranslate: 'Next'"
                                (buttonClick)="next()"
                            ></platform-button>
                        </div>
                    </div>
                </div>
            </div>
        }
        <router-outlet></router-outlet>
        @if (users && users.length > 0) {
            <p-sidebar
                [(visible)]="userSearchVisible"
                position="right"
                styleClass="md:w-8 sm:w-12 lg:w-4 "
                (onHide)="userSearchCancelButtonClick()"
            >
                @if (userSearchVisible) {
                    <ng-template pTemplate="header">
                        <span class="font-semibold text-xl" style="padding-left:0.75rem">{{
                            panelHeader | pTranslate
                        }}</span>
                    </ng-template>
                    <div class="flex flex-column flex-1">
                        <div class="flex-1 flex flex-column overflow-auto">
                            <div class="flex-1 flex overflow-auto">
                                @if (users && users.length > 0) {
                                    <platform-fuzzy-user-search
                                        class="flex-1 flex-column h-full overflow-hidden flex"
                                        [users]="users"
                                        [appliedStyle]="userSearchAppliedStyle"
                                        (onSelect)="handleUserSearch($event)"
                                        [title]="panelSubtitle"
                                        [selectedUser]="currentSelectedUser"
                                    ></platform-fuzzy-user-search>
                                }
                            </div>
                            <div class="flex justify-content-end pt-3 border-top-1 surface-border gap-3">
                                <platform-button
                                    [buttonType]="'button'"
                                    [text]="true"
                                    [label]="'default-reassign-buttonCancel' | pTranslate: 'Cancel'"
                                    [styleClass]="'w-8rem'"
                                    (buttonClick)="userSearchCancelButtonClick()"
                                ></platform-button>
                                <platform-button
                                    [buttonType]="'button'"
                                    [label]="'default-reassign-buttonConfirm' | pTranslate: 'Confirm'"
                                    [styleClass]="'w-8rem'"
                                    [disabled]="!this.currentSelectedUser"
                                    (buttonClick)="userSearchConfirmButtonClick()"
                                ></platform-button>
                            </div>
                        </div>
                    </div>
                }
            </p-sidebar>
        }
    `,
    imports: [
        ConfigurableButtonComponent,
        StepperComponent,
        InfoBarComponent,
        TableComponent,
        SidebarModule,
        FuzzyUserSearchComponent,
        RouterOutlet,
        PanelModule,
        NgClass,
        PUITranslatePipe,
    ],
})
export class ReassignmentComponent implements OnInit, OnDestroy {
    // Dependency Injection
    uiManagerService = inject(UIManagerService);
    reassignmentService = inject(ReassignmentService);
    bulkOperationService = inject(BulkOperationService);
    adminService = inject(AdminService);
    userService = inject(UserService);
    navigationService = inject(NavigationService);
    private router = inject(Router);
    private activateRoute = inject(ActivatedRoute);
    private confirmationService = inject(ConfirmationService);
    private messageService = inject(MessageService);

    // Variables
    navigationSubscription: Subscription | null = null;
    infoBarMessage = '';
    groupedCases: any[] = [];
    ready = false;
    userSearchVisible = false;
    users: User[] = [];
    currentSelectedUser: User | null = null;
    steps = [
        { order: 1, name: 'default-reassign-beforeReassignment' },
        { order: 2, name: 'default-reassign-beforeReassignmentSummary' },
    ];
    currentStepName = 'default-reassign-beforeReassignment';
    tableOperation: 'bulk' | 'single' | '' = '';
    selectedGroup = '';
    panelHeader = 'default-reassign-beforeReassignment';
    panelSubtitle = 'default-reassign-beforeReassignmentSubtitle';
    selectedCaseForReassignment: CaseRootInstance | null = null;
    userSearchAppliedStyle: {
        selectedItemColor: string;
        selectedItemBgColor: string;
        titleColor: string;
        itemColor: string;
        itemBgColor: string;
    } = {
        selectedItemColor: 'var(--alpha-text-color)',
        selectedItemBgColor: 'var(--alpha-secondary-fill-color)',
        titleColor: 'var(--alpha-text-color)',
        itemColor: 'var(--alpha-sidebar-color)',
        itemBgColor: 'var(--alpha-table-header-background-color)',
    };

    // These are percentages for calculating colors for bandwidth and daily limits.
    availabilityThresholds = window.environments.AVAILABILITY_THRESHOLDS || '35,70,100';

    groupHasAssignees: Map<string, boolean> = new Map();

    paginationConfigByGroup: Map<string, PaginationConfig> = new Map();
    isSelectedOwnedGroup = false;
    currentSelectedUsername = '';

    ngOnInit() {
        if (this.reassignmentService.casesToReassign.length === 0) {
            this.navigationService.navigateToInbox();
        }

        // window.addEventListener('beforeunload', this.handleBeforeUnload.bind(this));
        const firstChild = this.activateRoute?.snapshot?.firstChild;
        if (!firstChild) {
            this.refresh();
        }
        this.navigationSubscription = this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                // Remove query parameters before comparing the URL
                const pathWithoutQuery = event.urlAfterRedirects.split('?')[0];
                if (pathWithoutQuery === '/home/reassignment') {
                    this.refresh();
                }
            }
        });
        this.availabilityThresholds = this.availabilityThresholds
            .split(',')
            .map(Number)
            .sort((a: number, b: number) => b - a);
    }

    ngOnDestroy() {
        this.users = [];
        if (this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
    }

    handleBeforeUnload(event: BeforeUnloadEvent) {
        if (this.reassignmentService.casesToReassign.length > 0) {
            event.preventDefault();
            return false;
        }
        return true;
    }

    async refresh() {
        const groupedOrOwned: { grouped: { group: string; cases: CaseRootInstance[] }[]; owned: CaseRootInstance[] } =
            this.reassignmentService.listGroupsNUsers();
        const totalCases = this.reassignmentService.casesToReassign.length;
        this.infoBarMessage = 'default-reassign-infoBarMessage';
        const groupedCases = [];
        const grouped = groupedOrOwned.grouped;
        const owned = groupedOrOwned.owned;

        if (Array.isArray(groupedOrOwned.grouped) && groupedOrOwned.grouped.length > 0) {
            for (let i = 0; i < grouped.length; i++) {
                const group = grouped[i];
                const builderInstance = this.reassignmentService.buildReassignmentTable({
                    caseIdArray: group.cases.map((v: CaseRootInstance) => {
                        return {
                            cid: v.caseInstance.cid,
                            taskId: v.taskInstance.taskId,
                        };
                    }),
                    clearAllActionOptions: {
                        disabled: !this.groupHasAssignees.get(group.group),
                    },
                });
                if (builderInstance) {
                    if (!this.paginationConfigByGroup.get(group.group)) {
                        this.paginationConfigByGroup.set(group.group, { pageSize: 10, currentPage: 1 });
                    }
                    const { data, tableConfiguration } = await builderInstance.view();
                    groupedCases.push({
                        group: group.group,
                        isOwnedTaskGroup: false,
                        table: {
                            data: data,
                            tableConfiguration,
                            builderInstance,
                            config: {
                                ...builderInstance.build(),
                            },
                        },
                    });
                }
            }
        }

        if (Array.isArray(groupedOrOwned.owned) && groupedOrOwned.owned.length > 0) {
            const ownedGroupName = 'default-reassign-owned';
            const builderInstance = this.reassignmentService.buildReassignmentTable({
                caseIdArray: owned.map((v: CaseRootInstance) => {
                    return {
                        cid: v.caseInstance.cid,
                        taskId: v.taskInstance.taskId,
                    };
                }),
                clearAllActionOptions: {
                    disabled: !this.groupHasAssignees.get(ownedGroupName),
                },
            });
            if (builderInstance) {
                if (!this.paginationConfigByGroup.get(ownedGroupName)) {
                    this.paginationConfigByGroup.set(ownedGroupName, { pageSize: 10, currentPage: 1 });
                }
                const { data, tableConfiguration } = await builderInstance.view();
                groupedCases.push({
                    group: ownedGroupName,
                    isOwnedTaskGroup: true,
                    table: {
                        data: data,
                        tableConfiguration,
                        builderInstance,
                        config: {
                            ...builderInstance.build(),
                        },
                    },
                });
            }
        }
        this.groupedCases = groupedCases;
        this.ready = true;
    }

    private fetchUsers(taskType: string, groupName: string): Observable<any[]> {
        if (taskType === 'teamTasks') {
            return this.userService.getUserInfo().pipe(
                first(),
                switchMap((getUserInfo: any) => {
                    if (!getUserInfo) {
                        return of([]);
                    }
                    return this.adminService.getReportees(getUserInfo.userInfo.username).pipe(
                        map((data: any) => {
                            return data.reportees?.reduce((acc: any[], user: any) => {
                                if (user.metadata) {
                                    acc.push({
                                        username: user.username,
                                        icons: this.getIconsAndUserBandWidth(user.metadata), // Uncomment and implement if needed
                                    });
                                } else {
                                    console.warn('Metadata missing for user ->', user.username);
                                }
                                return acc;
                            }, []);
                        }),
                    );
                }),
            );
        } else if (this.isSelectedOwnedGroup) {
            const fetchActiveUsers = true;
            return this.adminService.fetchUserByGroups(fetchActiveUsers).pipe(
                map((data) => {
                    if (!Array.isArray(data) || data.length == 0) {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Warning',
                            detail: 'No users are assigned to the selected group',
                        });
                        return [];
                    }
                    return data.map((user) => ({
                        username: user.userName,
                        icons: this.getIconsAndUserBandWidth(user),
                    }));
                }),
            );
        } else if (taskType === 'groupTasks' || taskType === 'tasks') {
            return this.adminService.fetchGroupUsers(groupName, true).pipe(
                map((data) => {
                    if (!Array.isArray(data) || data.length == 0) {
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Warning',
                            detail: 'No users are assigned to the selected group',
                        });
                        return [];
                    }
                    return data.map((user: any) => ({
                        username: user.userName,
                        icons: this.getIconsAndUserBandWidth(user),
                    }));
                }),
            );
        } else {
            return of([]);
        }
    }

    // Then, create a method to handle both bulk and single clicks
    handleClick(groupName: string, row: any = null, operation: 'bulk' | 'single') {
        this.tableOperation = operation;
        this.selectedGroup = groupName;
        this.panelHeader = operation === 'bulk' ? 'default-reassign-panelHeader' : 'default-reassign-panelHeaderSingle';
        this.panelSubtitle =
            operation === 'bulk' ? 'default-reassign-panelSubtitle' : 'default-reassign-panelSubtitleSingle';
        const taskType = this.reassignmentService.inboxType || 'groupTasks';

        if (operation === 'single') {
            this.selectedCaseForReassignment = row;
        }

        this.fetchUsers(taskType, groupName).subscribe((users: any[]) => {
            this.users = users;

            if (operation === 'single' && this.currentSelectedUsername) {
                this.users = this.users.map((user: any) => {
                    if (this.currentSelectedUsername === user.username) {
                        this.currentSelectedUser = user;
                    }
                    return user;
                });
            }

            this.userSearchVisible = true;
        });
    }

    // Now, update your original methods to use the new handleClick method
    async handleBulkClick(groupName: string, isOwnedTaskGroup: boolean = false) {
        this.isSelectedOwnedGroup = isOwnedTaskGroup;
        this.currentSelectedUsername = '';
        this.handleClick(groupName, null, 'bulk');
    }

    handleSingleClick(groupName: string, row: any, isOwnedTaskGroup: boolean = false) {
        this.isSelectedOwnedGroup = isOwnedTaskGroup;
        this.currentSelectedUsername = row['assignedTo'];
        this.handleClick(groupName, row, 'single');
    }

    handleUserSearch(selectedUser: User) {
        this.currentSelectedUser = selectedUser;
    }

    userSearchCancelButtonClick() {
        this.currentSelectedUser = null;
        this.currentSelectedUsername = '';
        this.resetUserSearch();
    }

    reassignmentCancelButtonClick() {
        this.confirmationService.confirm({
            message:
                ap.language.translateConfig('{{ lang.default-reassign-cancelConfirmationMessage }}') ||
                'Are you sure you want to cancel?',
            header: ap.language.translateConfig('{{ lang.default-reassign-confirm }}') || 'Confirm',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.navigationService.navigateToInbox();
            },
        });
    }

    userSearchConfirmButtonClick() {
        this.resetUserSearch();
        if (this.tableOperation === 'bulk') {
            const cases: any = this.getRowdataByGroup(this.selectedGroup);
            this.reassignmentService.reassignCasesTo(cases, this.currentSelectedUser?.username || '');
        }

        if (this.tableOperation === 'single') {
            this.reassignmentService.reassignCasesTo(
                [this.selectedCaseForReassignment as CaseRootInstance],
                this.currentSelectedUser?.username || '',
            );
        }

        this.setGroupAssigneeStatus(this.selectedGroup, true);
        this.refresh();
        this.currentSelectedUser = null;
    }

    getRowdataByGroup(groupname: string) {
        const groupCase = this.groupedCases.find((group) => group.group === groupname);
        const builderInstance: TableConfigBuilder = groupCase.table.builderInstance;
        return builderInstance.getRows();
    }

    next(): void {
        if (!this.validateAssignee()) {
            this.messageService.add({
                severity: 'error',
                summary: ap.language.translateConfig('{{ lang.default-reassign-snackbarError }}'),
                detail: ap.language.translateConfig('{{ lang.default-reassign-snackbarAssigneeError }}'),
            });
            return;
        }

        this.ready = false;
        this.navigationService.navigateToReassignmentBefore();
    }

    validateAssignee(): boolean {
        const bulkAssignment = this.reassignmentService.formatReassignmentDataForBulkOperation();
        return bulkAssignment.data.every((el: ReassignmentDto) => !!el.assignedTo);
    }

    resetUserSearch() {
        this.userSearchVisible = false;
        // this.users = [];
    }

    trackByGroup(index: number, item: any) {
        return item.group;
    }

    getIconsAndUserBandWidth(user: GroupUser) {
        user.remainingBandwidth = user.remainingBandwidth || 0;
        user.remainingDailyLimit = user.remainingDailyLimit || 0;
        user.dailyLimit = user.dailyLimit || 0;
        user.bandwidth = user.bandwidth || 0;
        const reDailyLimitcolor = this.calculateAvailabilityThresholdColor(user.dailyLimit, user.remainingDailyLimit);
        const reBandwidthcolor = this.calculateAvailabilityThresholdColor(user.bandwidth, user.remainingBandwidth);
        return [
            {
                icon: 'pi pi-gauge',
                value: user?.remainingBandwidth,
                label: 'default-reassign-tooltipBandwidth',
                color: reBandwidthcolor,
            },
            {
                icon: 'pi pi-calendar',
                value: user?.remainingDailyLimit,
                label: 'default-reassign-tooltipDailyLimit',
                color: reDailyLimitcolor,
            },
        ];
    }

    calculateAvailabilityThresholdColor(actualValue: number, remainingValue: number): string {
        const value = (remainingValue / actualValue) * 100;
        if (value <= this.availabilityThresholds[2]) {
            return 'var(--alpha-error-color)';
        } else if (value <= this.availabilityThresholds[1]) {
            return 'var(--alpha-warning-color)';
        } else if (value <= this.availabilityThresholds[0]) {
            return 'var(--alpha-primary-color)';
        }
        return 'var(--alpha-text-color)';
    }

    async handleAfterViewInit(event: CustomCodeEventDetail) {
        await this.reassignmentService.executeAfterViewInitAction(event);
    }

    handleClearAllClick(groupName: string) {
        const rowdData: any = this.getRowdataByGroup(groupName);
        this.reassignmentService.reassignCasesTo(rowdData, '');
        this.setGroupAssigneeStatus(groupName, false);
        this.refresh();
    }

    setGroupAssigneeStatus(groupName: string, value: boolean): void {
        if (this.groupHasAssignees.has(groupName)) {
            this.groupHasAssignees.set(groupName, value);
        } else {
            this.groupHasAssignees.set(groupName, value);
        }
    }

    onPageChange(event: number, group: string) {
        const config = this.paginationConfigByGroup.get(group);
        if (config) {
            config.currentPage = event;
            this.paginationConfigByGroup.set(group, config);
        }
    }

    onPageSizeChange(event: number, group: string) {
        const config = this.paginationConfigByGroup.get(group);
        if (config) {
            config.pageSize = event;
            config.currentPage = 1;
            this.paginationConfigByGroup.set(group, config);
        }
    }
}
